import { Link } from "react-router-dom";
import { TypeIcon } from "../common/TypeIcon";
import { daysBetweenKeys, formatDayKey } from "../../utils/calendarDay";
import { formatTime } from "../../utils/tripDisplay";
import {
  clockMinutes,
  formatMinutes,
  listNames,
  percentUnderPosted,
  type RecapActivity,
  type RecapDay,
  type TripRecap,
} from "../../utils/tripRecap";
import "./TripStory.css";

interface TripStoryProps {
  tripId: string;
  name: string;
  dates: string;
  party: string[];
  /** The trip's first day, "YYYY-MM-DD", for numbering the days. */
  startKey: string | null;
  recap: TripRecap;
  onPlayRecap: () => void;
  onShowItinerary: () => void;
}

const TRAVEL_TYPES = new Set(["TRANSPORTATION", "AIRPORT", "SHIP"]);

function dayTitle(day: RecapDay, all: RecapActivity[]): string {
  if (day.places.length > 0) return listNames(day.places);
  if (all.some((a) => a.dayKey === day.dayKey && a.type && TRAVEL_TYPES.has(a.type))) return "Travel day";
  return "";
}

/** The day's rides, shows and food along its hours, tinted by kind. */
function DayStrip({ day, activities }: { day: RecapDay; activities: RecapActivity[] }) {
  const timed = activities.filter((a) => a.dayKey === day.dayKey && a.start);
  if (timed.length < 2) return null;
  const span = (a: RecapActivity) => {
    const from = clockMinutes(a.start!, a.zone);
    let to = a.end ? clockMinutes(a.end, a.zone) : from + 5;
    // Ran past midnight: run it to the edge of the day.
    if (to < from) to = 24 * 60;
    return [from, Math.max(to, from + 4)] as const;
  };
  const spans = timed.map((a) => ({ a, s: span(a) }));
  const lo = Math.floor(Math.min(...spans.map((x) => x.s[0])) / 60) * 60;
  const hi = Math.min(24 * 60, Math.max(lo + 6 * 60, Math.ceil(Math.max(...spans.map((x) => x.s[1])) / 60) * 60));
  const pct = (m: number) => `${((Math.min(hi, Math.max(lo, m)) - lo) / (hi - lo)) * 100}%`;
  const width = (s: readonly [number, number]) => `${((Math.min(hi, s[1]) - Math.max(lo, s[0])) / (hi - lo)) * 100}%`;
  const hourLabel = (m: number) => {
    const h = Math.floor(m / 60) % 24;
    return h === 0 ? "12a" : h === 12 ? "noon" : h < 12 ? `${h}a` : `${h - 12}p`;
  };
  const midTicks = [];
  for (let m = lo + 3 * 60; m < hi - 60; m += 3 * 60) midTicks.push(m);
  // Parks first, so the rides inside them draw on top.
  const ordered = [...spans].sort((x, y) => (x.a.kind === "park" ? 0 : 1) - (y.a.kind === "park" ? 0 : 1));
  return (
    <div className="ts-strip" aria-hidden="true">
      <div className="ts-bar">
        {ordered.map(({ a, s }) => (
          <i
            key={a.id}
            className={`k-${a.type && TRAVEL_TYPES.has(a.type) ? "travel" : a.kind}${a.gaveUp ? " quit" : ""}`}
            style={{ left: pct(s[0]), width: width(s) }}
          />
        ))}
      </div>
      <div className="ts-lab">
        <span style={{ left: 0 }}>{hourLabel(lo)}</span>
        {midTicks.map((m) => (
          <span key={m} style={{ left: pct(m), transform: "translateX(-50%)" }}>{hourLabel(m)}</span>
        ))}
        <span style={{ left: "100%", transform: "translateX(-100%)" }}>{hourLabel(hi)}</span>
      </div>
    </div>
  );
}

/** Up to three moments that tell the day: how it started, the longest line, the first meal. */
function dayMoments(day: RecapDay): RecapActivity[] {
  const picks: RecapActivity[] = [];
  const add = (a: RecapActivity | undefined | null) => {
    if (a && !picks.some((p) => p.name === a.name) && picks.length < 3) picks.push(a);
  };
  const doing = day.moments.filter((m) => m.kind !== "food" && !m.gaveUp);
  add(doing[0]);
  add(doing.reduce<RecapActivity | null>((b, m) => ((m.waited ?? 0) > (b?.waited ?? 0) ? m : b), null));
  add(day.moments.find((m) => m.kind === "food"));
  for (const m of doing) add(m);
  return picks;
}

function momentCaption(a: RecapActivity): string {
  const parts: string[] = [];
  if (a.waited != null && a.kind !== "food") parts.push(`${a.waited}m wait`);
  if (a.start) parts.push(formatTime(a.start, a.zone));
  return parts.join(" · ");
}

/** Story / Itinerary, for a finished trip: on the story's cover and the itinerary's header card. */
export function TripViewSwitch({
  view,
  onChange,
}: {
  view: "story" | "itinerary";
  onChange: (view: "story" | "itinerary") => void;
}) {
  return (
    <div className="ts-switch" role="group" aria-label="Trip view">
      <button type="button" aria-pressed={view === "story"} onClick={() => onChange("story")}>Story</button>
      <button type="button" aria-pressed={view === "itinerary"} onClick={() => onChange("itinerary")}>Itinerary</button>
    </div>
  );
}

/** A finished trip read as a story: cover, headline numbers, the recap, then a chapter per day. */
export function TripStory({ tripId, name, dates, party, startKey, recap, onPlayRecap, onShowItinerary }: TripStoryProps) {
  const pv = recap.postedVsWaited;
  const facts: [string, string][] = [];
  if (recap.rides > 0) facts.push([String(recap.rides), recap.rides === 1 ? "ride" : "rides"]);
  if (recap.shows > 0) facts.push([String(recap.shows), recap.shows === 1 ? "show" : "shows"]);
  if (recap.waited > 0) facts.push([formatMinutes(recap.waited).replace(/ \d+m$/, ""), "in line"]);
  if (pv && pv.count >= 3) {
    const p = percentUnderPosted(pv);
    facts.push([`${Math.abs(p)}%`, p >= 0 ? "under posted" : "over posted"]);
  }
  if (recap.food > 0) facts.push([String(recap.food), "food stops"]);
  if (facts.length < 4) facts.push([String(recap.days.length), recap.days.length === 1 ? "day" : "days"]);

  return (
    <div className="trip-story">
      {/* One cover holds everything above the first day: the view switch,
          the numbers and the recap. The way back is in the top bar. */}
      <header className="ts-cover">
        <div className="ts-cover-top">
          <TripViewSwitch view="story" onChange={(v) => v === "itinerary" && onShowItinerary()} />
        </div>
        <div className="ts-cover-in">
          <span className="ts-chip">{dates}</span>
          <h1 className="ts-title">{name}</h1>
          {party.length > 0 && <div className="ts-sub">With {listNames(party)}</div>}
        </div>
        <div className="ts-facts">
          {facts.slice(0, 4).map(([v, k]) => (
            <div key={k}>
              <b>{v}</b>
              <small>{k}</small>
            </div>
          ))}
        </div>
        <button type="button" className="ts-play" onClick={onPlayRecap}>
          <span className="ts-play-dot" aria-hidden="true">▶</span>
          Play the recap
        </button>
      </header>

      {recap.days.map((day) => {
        const n = startKey ? daysBetweenKeys(startKey, day.dayKey) + 1 : null;
        const title = dayTitle(day, recap.activities);
        const moments = dayMoments(day);
        return (
          <section key={day.dayKey} className="ts-day" aria-label={formatDayKey(day.dayKey, { weekday: "long", month: "long", day: "numeric" })}>
            <div className="ts-dayh">
              <h2>
                {formatDayKey(day.dayKey, { weekday: "long" })}
                {title ? ` · ${title}` : ""}
              </h2>
              <small>{n != null && n >= 1 ? `DAY ${n}` : formatDayKey(day.dayKey, { month: "short", day: "numeric" })}</small>
            </div>
            <DayStrip day={day} activities={recap.activities} />
            {day.summary && <p className="ts-note">{day.summary}</p>}
            {moments.length > 0 && (
              <div className="ts-moments">
                {moments.map((m) => (
                  <Link key={m.id} to={`/trips/${tripId}/interactions/${m.id}`} className="ts-mo">
                    <span className="ts-mo-ic">
                      <TypeIcon type={m.type} name={m.name} tile />
                    </span>
                    <span className="ts-mo-name">{m.name}</span>
                    <small>{momentCaption(m)}</small>
                  </Link>
                ))}
              </div>
            )}
            <div className="ts-chips">
              {day.rides > 0 && <span className="ts-c ride">{day.rides} {day.rides === 1 ? "ride" : "rides"}</span>}
              {day.shows > 0 && <span className="ts-c show">{day.shows} {day.shows === 1 ? "show" : "shows"}</span>}
              {day.food > 0 && <span className="ts-c food">{day.food} food</span>}
              {day.waited > 0 && <span className="ts-c">{formatMinutes(day.waited)} in line</span>}
              {day.first && day.last && day.last > day.first && (
                <span className="ts-c">
                  {formatTime(day.first, day.zone)} – {formatTime(day.last, day.zone)}
                </span>
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}
